// @ts-check
/**
 * @file realtime.js
 * @location src/integrations/supabase/realtime.js
 * @role 팀 데이터 실시간 구독 — tasks/members/task_templates 변경을 reducer 로 반영.
 *
 * Realtime 은 SELECT RLS 를 적용하므로 본인 팀(가시 범위) 변경만 수신.
 * INSERT/UPDATE → SYNC_*_UPSERT, DELETE → SYNC_*_REMOVE.
 *
 *  callbacks:
 *    onReconnect()  — 채널이 SUBSCRIBED 상태가 됐을 때 (초기 + 재구독 후)
 *                     호출 측이 refetchWorkspace 로 끊김 동안의 변경 회수
 *    onDisconnect() — CHANNEL_ERROR / TIMED_OUT / CLOSED 시. UI 인디케이터 표시 트리거
 */
import { supabase } from './client.js';
import { rowToTask, rowToMember, rowToTemplate } from './mappers.js';

const TABLES = [
  { table: 'tasks',          upsert: 'SYNC_TASK_UPSERT',     remove: 'SYNC_TASK_REMOVE',     map: rowToTask,     key: 'task' },
  { table: 'members',        upsert: 'SYNC_MEMBER_UPSERT',   remove: 'SYNC_MEMBER_REMOVE',   map: rowToMember,   key: 'member' },
  { table: 'task_templates', upsert: 'SYNC_TEMPLATE_UPSERT', remove: 'SYNC_TEMPLATE_REMOVE', map: rowToTemplate, key: 'template' },
];

/**
 * 팀 워크스페이스 변경 구독.
 * @param {Function} dispatch - store dispatch
 * @param {{ onReconnect?: () => void, onDisconnect?: () => void }} [callbacks]
 * @returns {() => void} 구독 해제 함수
 */
export function subscribeWorkspace(dispatch, callbacks = {}) {
  const channel = supabase.channel('workspace');

  for (const cfg of TABLES) {
    channel.on(
      'postgres_changes',
      { event: '*', schema: 'public', table: cfg.table },
      (payload) => {
        if (payload.eventType === 'DELETE') {
          const id = payload.old?.id;
          if (id) dispatch({ type: cfg.remove, id });
        } else {
          dispatch({ type: cfg.upsert, [cfg.key]: cfg.map(payload.new) });
        }
      },
    );
  }

  channel.subscribe((status) => {
    if (status === 'SUBSCRIBED') {
      callbacks.onReconnect?.();
    } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
      callbacks.onDisconnect?.();
    }
  });

  return () => { supabase.removeChannel(channel); };
}
